/**
 * Map Component
 * Renders the Leaflet map with esri-leaflet layers
 */

import React, { useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet.markercluster';
import * as esri from 'esri-leaflet';
import { mapConfig, arcgisConfig, featureServices, symbologyConfig, isEmissionsTenant } from '../config/gisConfig';
import { milesToMeters } from '../services/gisService';
import MapLegend from './MapLegend';
import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';

// Fix for default marker icons in Leaflet with React
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

// Compute a leaflet LatLng for the centroid of a feature (handles ArcGIS + GeoJSON shapes)
function featureCentroidLatLng(f) {
  const geom = f?.geometry || (f.feature && f.feature.geometry);
  if (!geom) return null;
  if (geom.x !== undefined && geom.y !== undefined) return L.latLng(geom.y, geom.x);
  if (geom.rings && geom.rings[0]) {
    const ring = geom.rings[0];
    const ll = ring.reduce((acc, c) => [acc[0] + c[1], acc[1] + c[0]], [0, 0]);
    return L.latLng(ll[0] / ring.length, ll[1] / ring.length);
  }
  if (geom.type === 'Point') return L.latLng(geom.coordinates[1], geom.coordinates[0]);
  if (geom.type === 'Polygon' || geom.type === 'MultiPolygon') {
    const ring = geom.type === 'Polygon' ? geom.coordinates[0] : geom.coordinates[0][0];
    const ll = ring.reduce((acc, c) => [acc[0] + c[1], acc[1] + c[0]], [0, 0]);
    return L.latLng(ll[0] / ring.length, ll[1] / ring.length);
  }
  if (geom.type === 'LineString') {
    const c = geom.coordinates[Math.floor(geom.coordinates.length / 2)];
    return L.latLng(c[1], c[0]);
  }
  return null;
}

// Ray-cast point-in-polygon test against an array of leaflet LatLngs (single ring).
function pointInPolygon(latlng, polyLatLngs) {
  // Flatten if it's nested ([[ring]])
  const ring = Array.isArray(polyLatLngs[0]) ? polyLatLngs[0] : polyLatLngs;
  if (!ring || ring.length === 0) return false;
  const x = latlng.lng, y = latlng.lat;
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const xi = ring[i].lng, yi = ring[i].lat;
    const xj = ring[j].lng, yj = ring[j].lat;
    const intersect = ((yi > y) !== (yj > y)) && (x < ((xj - xi) * (y - yi)) / (yj - yi + 1e-12) + xi);
    if (intersect) inside = !inside;
  }
  return inside;
}

// Basemap providers — free public tiles, no API key required.
const BASEMAPS = {
  light: {
    label: 'Light',
    url: 'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png',
    options: { attribution: '&copy; OpenStreetMap &copy; CARTO', subdomains: 'abcd', maxZoom: 19 }
  },
  satellite: {
    label: 'Satellite',
    url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
    options: { attribution: 'Tiles &copy; Esri', maxZoom: 19 }
  },
  terrain: {
    label: 'Terrain',
    url: 'https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png',
    options: { attribution: '&copy; OpenTopoMap (CC-BY-SA), &copy; OpenStreetMap', maxZoom: 17, subdomains: 'abc' }
  },
  streets: {
    label: 'Streets',
    url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
    options: { attribution: '&copy; OpenStreetMap contributors', maxZoom: 19 }
  }
};

function Map({ queryResults, onFeatureClick, symbologyOverrides = { tractTypes: {}, wellStatuses: {} }, scadaAlerts = [], onScadaAssetClick, mapApiRef, onLayerToggle }) {
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const layersRef = useRef({});
  const markersRef = useRef(null);
  const polygonsRef = useRef(null); // Separate layer for polygons (not clustered)
  const labelsLayerRef = useRef(null); // Layer group for labels
  const scadaLayerRef = useRef(null);  // SCADA alert markers (UOC-style overlay)
  const basemapRef = useRef(null);     // Currently-active basemap tile layer
  const importedLayerRef = useRef(null); // User-imported features layer
  const annotationLayerRef = useRef(null); // Annotation overlay
  const selectionLayerRef = useRef(null);  // Drawn selection geometry
  const featureCacheRef = useRef([]);   // Last query results, used by Export
  const selectedFeaturesRef = useRef([]); // Features currently selected via draw tool
  const currentLayerNameRef = useRef('export');
  const highlightLayerRef = useRef(null); // Highlight overlay for fly-to clicks
  const importedLayersListRef = useRef([]); // [{ id, name, layerGroup, count }]
  const [importedLayers, setImportedLayers] = useState([]);
  const [layerVisibility, setLayerVisibility] = useState({});

  // Debug: Log layer visibility state changes
  useEffect(() => {
    console.log('📊 Layer visibility state:', layerVisibility);
  }, [layerVisibility]);

  // Auto-select layer when query results arrive
  useEffect(() => {
    if (queryResults && queryResults.layer) {
      const layer = queryResults.layer;

      // Handle multi-layer results
      if (layer === 'multi' && queryResults.layers) {
        console.log(`🌟 Auto-selecting multi-layers: ${queryResults.layers.join(', ')}`);
        const newVisibility = {};
        queryResults.layers.forEach(l => {
          newVisibility[l] = true;
        });
        setLayerVisibility(prev => {
          const updated = { ...prev, ...newVisibility };
          console.log('🔄 Setting multi-layer visibility:', updated);
          return updated;
        });
        return;
      }

      // Map 'proximity' and 'tpl-tracts' to 'tracts'
      const normalizedLayer = layer === 'proximity' || layer === 'tpl-tracts' ? 'tracts' : layer;

      // Layer-group expansion: certain queries imply multiple related layers should
      // come on together. e.g. fenceline → sensors+facility+plumes for a complete picture.
      const LAYER_GROUPS = {
        fenceline:           ['fenceline', 'sensors', 'facility', 'process_units', 'dispersion_plumes'],
        dispersion_plumes:   ['dispersion_plumes', 'sensors', 'facility'],
        sensors:             ['sensors', 'facility'],
        pipe_risk:           ['pipe_risk', 'distribution_mains'],
        crews:               ['crews', 'work_orders_open'],
        sensitive_premises:  ['sensitive_premises', 'distribution_mains']
      };
      const layersToEnable = LAYER_GROUPS[normalizedLayer] || [normalizedLayer];

      if (featureServices[normalizedLayer] || layersToEnable.length > 1) {
        console.log(`🎯 Auto-selecting layer group: ${layersToEnable.join(', ')}`);
        setLayerVisibility(prev => {
          const updated = { ...prev };
          layersToEnable.forEach((l) => {
            if (featureServices[l]) updated[l] = true;
          });
          return updated;
        });
        // Also fetch the auxiliary layers (everything beyond the primary intent layer) so
        // their features actually render on the map — auto-enable was only setting visibility,
        // not loading data. PM #6: fenceline query → facility polygon should appear.
        if (onLayerToggle && layersToEnable.length > 1) {
          layersToEnable.forEach((l) => {
            if (l !== normalizedLayer && featureServices[l]) {
              // Defer slightly so primary results render first, avoids race.
              setTimeout(() => onLayerToggle(l, true), 150);
            }
          });
        }
      }
    } else if (queryResults === null) {
      // Clear was pressed - uncheck all layers
      console.log('🧹 Clearing layer visibility');
      setLayerVisibility({});
    }
  }, [queryResults]);

  // ─── UOC-style SCADA alert overlay ────────────────────────────────────
  // Always-on layer with severity-colored pulsing markers per active alert.
  // Click → triggers onScadaAssetClick(asset_id) which opens that asset's dossier.
  useEffect(() => {
    if (!mapRef.current) return;
    // Tear down any previous layer
    if (scadaLayerRef.current) {
      mapRef.current.removeLayer(scadaLayerRef.current);
      scadaLayerRef.current = null;
    }
    if (!scadaAlerts || scadaAlerts.length === 0) return;

    const sevColor = { critical: '#dc2626', warning: '#d97706', info: '#2563eb' };
    const layer = L.layerGroup();
    scadaAlerts.forEach((a) => {
      if (a.lat == null || a.lon == null) return;
      const color = sevColor[a.severity] || '#6b7280';
      const pulseClass = a.severity === 'critical' ? 'scada-marker-pulse' : '';
      const html = `
        <div class="scada-marker ${pulseClass}" style="--scada-color:${color}">
          <div class="scada-marker-dot"></div>
          <div class="scada-marker-ring"></div>
        </div>`;
      const icon = L.divIcon({
        className: 'scada-marker-icon',
        html,
        iconSize: [22, 22],
        iconAnchor: [11, 11],
      });
      const popupHtml = `
        <div class="scada-popup">
          <div class="scada-popup-sev" style="background:${color}">${a.severity.toUpperCase()}</div>
          <div class="scada-popup-title">${a.asset_name}</div>
          <div class="scada-popup-desc">${a.title}</div>
          <div class="scada-popup-meta">
            ${a.code} · ${a.county} County · ${a.impact_bbl_day?.toLocaleString() || 0} bbl/day
          </div>
          <button class="scada-popup-btn" data-asset="${a.asset_id}">View dossier →</button>
        </div>`;
      const marker = L.marker([a.lat, a.lon], { icon, riseOnHover: true });
      marker.bindPopup(popupHtml, { maxWidth: 280, className: 'scada-popup-wrap' });
      marker.on('popupopen', (e) => {
        const btn = e.popup.getElement()?.querySelector('.scada-popup-btn');
        if (btn) {
          btn.addEventListener('click', () => {
            if (typeof onScadaAssetClick === 'function') onScadaAssetClick(a.asset_id);
            e.target.closePopup();
          });
        }
      });
      layer.addLayer(marker);
    });
    layer.addTo(mapRef.current);
    scadaLayerRef.current = layer;
  }, [scadaAlerts, onScadaAssetClick]);

  // Initialize map
  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current) return;

    // Create map
    const map = L.map(mapContainerRef.current, {
      center: mapConfig.center,
      zoom: mapConfig.zoom,
      minZoom: mapConfig.minZoom,
      maxZoom: mapConfig.maxZoom
    });

    // Default basemap: Light. Switchable via mapApi.setBasemap.
    const defaultBasemapKey = (typeof window !== 'undefined' && window.localStorage.getItem('nexus_basemap')) || 'light';
    const initialBasemap = BASEMAPS[defaultBasemapKey] || BASEMAPS.light;
    basemapRef.current = L.tileLayer(initialBasemap.url, initialBasemap.options).addTo(map);

    // Feature layers are loaded only when queried (not as persistent layers)
    // This avoids 400 errors from public services that don't support tile requests
    Object.keys(featureServices).forEach(layerKey => {
      setLayerVisibility(prev => ({ ...prev, [layerKey]: false }));
    });

    mapRef.current = map;

    // Annotation overlay layer
    annotationLayerRef.current = L.layerGroup().addTo(map);
    selectionLayerRef.current = L.layerGroup().addTo(map);
    importedLayerRef.current = L.layerGroup().addTo(map);

    // Hydrate any persisted annotations on first load
    import('../services/demoExtrasService').then(({ fetchAnnotations }) => {
      fetchAnnotations().then(list => list.forEach(addAnnotationToMap)).catch(() => {});
    });

    // Highlight overlay for fly-to clicks from chat tables
    highlightLayerRef.current = L.layerGroup().addTo(map);
    const flyToHandler = (e) => {
      const { id, layer } = e.detail || {};
      if (!id || !mapRef.current) return;
      // Search the feature cache for matching id
      const found = (featureCacheRef.current || []).find((f) => {
        const p = f.properties || f.attributes || {};
        return p.id === id || p.crew_id === id || p.sensor_id === id;
      });
      if (!found || !found.geometry) {
        // Layer might not be loaded — toggle it on first via onLayerToggle (handled by parent)
        console.warn(`fly-to: ${id} not in cache, request layer toggle for ${layer}`);
        window.dispatchEvent(new CustomEvent('nexus-request-layer-load', { detail: { layer, then: { id } } }));
        return;
      }
      // Compute centroid + pan + flash highlight
      let center = null;
      const g = found.geometry;
      if (g.type === 'Point') center = [g.coordinates[1], g.coordinates[0]];
      else if (g.type === 'LineString') {
        const c = g.coordinates[Math.floor(g.coordinates.length / 2)];
        center = [c[1], c[0]];
      } else if (g.type === 'Polygon') {
        const ring = g.coordinates[0];
        const avg = ring.reduce((acc, c) => [acc[0] + c[0], acc[1] + c[1]], [0, 0]);
        center = [avg[1] / ring.length, avg[0] / ring.length];
      }
      if (!center) return;
      mapRef.current.flyTo(center, Math.max(mapRef.current.getZoom(), 15), { duration: 0.8 });
      // Drop a temporary highlight ring
      highlightLayerRef.current.clearLayers();
      const ring = L.circleMarker(center, { radius: 18, color: '#fbbf24', fillColor: '#fde68a', fillOpacity: 0.4, weight: 3 });
      ring.bindTooltip(id, { permanent: true, direction: 'top', offset: [0, -22], className: 'nexus-highlight-tooltip' });
      ring.addTo(highlightLayerRef.current);
      // Pulse: animate the radius briefly
      let r = 18;
      let growing = true;
      const interval = setInterval(() => {
        r += growing ? 1 : -1;
        if (r > 26) growing = false;
        if (r < 18) growing = true;
        ring.setRadius(r);
      }, 80);
      setTimeout(() => { clearInterval(interval); highlightLayerRef.current.clearLayers(); }, 4000);
    };
    window.addEventListener('nexus-fly-to', flyToHandler);

    return () => {
      window.removeEventListener('nexus-fly-to', flyToHandler);
      map.remove();
      mapRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onFeatureClick]);

  // Helper: render an annotation onto the map
  const addAnnotationToMap = (ann) => {
    if (!annotationLayerRef.current || !ann) return;
    const color = ann.color || '#dc2626';
    const popup = `<div style="min-width:180px"><strong>${ann.title || '(untitled)'}</strong><div style="color:#475569;font-size:11px;margin-top:2px">${ann.author} · ${new Date(ann.created_at).toLocaleString()}</div>${ann.note ? `<div style="margin-top:6px;font-size:13px">${ann.note}</div>` : ''}</div>`;
    if (ann.kind === 'pin' && ann.geometry?.type === 'Point') {
      const [lng, lat] = ann.geometry.coordinates;
      const m = L.circleMarker([lat, lng], { radius: 8, color, fillColor: color, fillOpacity: 0.7, weight: 2 });
      m.bindPopup(popup);
      m.addTo(annotationLayerRef.current);
    } else if (ann.geometry) {
      const layer = L.geoJSON(ann.geometry, { style: { color, fillColor: color, fillOpacity: 0.18, weight: 2, dashArray: '4 4' } });
      layer.bindPopup(popup);
      layer.addTo(annotationLayerRef.current);
    }
  };

  // Expose imperative API for the toolbar
  useEffect(() => {
    if (!mapApiRef) return;
    mapApiRef.current = {
      setBasemap: (id) => {
        const cfg = BASEMAPS[id];
        if (!cfg || !mapRef.current) return;
        if (basemapRef.current) mapRef.current.removeLayer(basemapRef.current);
        basemapRef.current = L.tileLayer(cfg.url, cfg.options).addTo(mapRef.current);
        try { window.localStorage.setItem('nexus_basemap', id); } catch (e) { /* ignore */ }
      },
      addImportedLayer: (fc) => {
        if (!importedLayerRef.current || !fc) return;
        const id = `imp_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
        const layerGroup = L.layerGroup();
        const layer = L.geoJSON(fc, {
          style: { color: '#7c3aed', weight: 2, fillColor: '#a78bfa', fillOpacity: 0.25 },
          pointToLayer: (feature, latlng) =>
            L.circleMarker(latlng, { radius: 6, color: '#7c3aed', fillColor: '#a78bfa', fillOpacity: 0.7, weight: 2 }),
          onEachFeature: (feature, layer) => {
            const props = feature.properties || {};
            const rows = Object.entries(props).slice(0, 8).map(([k, v]) => `<tr><td style="color:#64748b;padding:2px 8px 2px 0">${k}</td><td>${v}</td></tr>`).join('');
            layer.bindPopup(`<div><strong>${fc.name || 'Imported feature'}</strong><table style="margin-top:6px;font-size:12px">${rows}</table></div>`);
          }
        });
        layer.addTo(layerGroup);
        layerGroup.addTo(importedLayerRef.current);
        try {
          mapRef.current.fitBounds(layer.getBounds(), { padding: [20, 20] });
        } catch (e) { /* empty FC */ }
        // Track for the Layers panel remove control (PM #7).
        const entry = { id, name: fc.name || 'Imported layer', count: (fc.features || []).length, layerGroup };
        importedLayersListRef.current = [...importedLayersListRef.current, entry];
        setImportedLayers([...importedLayersListRef.current]);
        // Add into feature cache so Export "current" picks them up
        featureCacheRef.current = (featureCacheRef.current || []).concat((fc.features || []).map((f) => ({ ...f, _sourceLayer: id, _importedId: id })));
        currentLayerNameRef.current = fc.name || 'imported';
      },
      removeImportedLayer: (id) => {
        const entry = importedLayersListRef.current.find((e) => e.id === id);
        if (!entry) return;
        try { entry.layerGroup.remove(); } catch (e) { /* ignore */ }
        importedLayersListRef.current = importedLayersListRef.current.filter((e) => e.id !== id);
        setImportedLayers([...importedLayersListRef.current]);
        // Drop these features from the cache too
        featureCacheRef.current = (featureCacheRef.current || []).filter((f) => f._importedId !== id);
      },
      addAnnotationLayer: (ann) => addAnnotationToMap(ann),
      startSelection: async (mode) => {
        if (!mapRef.current) return;
        await import('leaflet-draw');
        await import('leaflet-draw/dist/leaflet.draw.css');
        // Clear any previous handler
        if (mapApiRef.current._drawer) {
          try { mapApiRef.current._drawer.disable(); } catch (e) { /* ignore */ }
        }
        const Draw = L.Draw || {};
        const drawer = mode === 'rectangle'
          ? new Draw.Rectangle(mapRef.current, { shapeOptions: { color: '#2563eb', weight: 2, fillOpacity: 0.1 } })
          : new Draw.Polygon(mapRef.current, { shapeOptions: { color: '#2563eb', weight: 2, fillOpacity: 0.1 }, allowIntersection: false });
        drawer.enable();
        mapApiRef.current._drawer = drawer;
        const handler = (e) => {
          const shape = e.layer;
          shape.addTo(selectionLayerRef.current);
          // Compute selected features = those in featureCacheRef whose centroid lies inside the drawn shape
          const polyLatLngs = shape.getLatLngs ? shape.getLatLngs() : [];
          const selected = (featureCacheRef.current || []).filter((f) => {
            const ll = featureCentroidLatLng(f);
            if (!ll) return false;
            return shape.getBounds().contains(ll) && pointInPolygon(ll, polyLatLngs);
          });
          selectedFeaturesRef.current = selected;
          mapRef.current.off(L.Draw.Event.CREATED, handler);
          // Visual feedback: re-bind a popup on the shape with selection count
          shape.bindPopup(`<strong>${selected.length}</strong> features selected<br/><span style="color:#64748b;font-size:11px">Click Export to download.</span>`).openPopup();
        };
        mapRef.current.on(L.Draw.Event.CREATED, handler);
      },
      clearSelection: () => {
        selectedFeaturesRef.current = [];
        if (selectionLayerRef.current) selectionLayerRef.current.clearLayers();
      },
      startAnnotation: async (kind, onComplete) => {
        if (!mapRef.current) return;
        await import('leaflet-draw');
        await import('leaflet-draw/dist/leaflet.draw.css');
        const Draw = L.Draw || {};
        if (kind === 'pin') {
          const m = mapRef.current;
          const onClick = (e) => {
            m.off('click', onClick);
            const geometry = { type: 'Point', coordinates: [e.latlng.lng, e.latlng.lat] };
            onComplete(geometry);
          };
          m.once('click', onClick);
        } else {
          const drawer = new Draw.Polygon(mapRef.current, { shapeOptions: { color: '#dc2626', weight: 2, fillOpacity: 0.18 }, allowIntersection: false });
          drawer.enable();
          const handler = (e) => {
            const layer = e.layer;
            const geo = layer.toGeoJSON();
            mapRef.current.off(L.Draw.Event.CREATED, handler);
            onComplete(geo.geometry);
          };
          mapRef.current.on(L.Draw.Event.CREATED, handler);
        }
      },
      getCurrentFeatures: () => (featureCacheRef.current || []).map((f) => ({ feature: f.feature || f })),
      getSelectedFeatures: () => (selectedFeaturesRef.current || []).map((f) => ({ feature: f.feature || f })),
      getCurrentLayerName: () => currentLayerNameRef.current
    };
  }, [mapApiRef]);

  // Track query results into feature cache for Export
  useEffect(() => {
    if (queryResults && Array.isArray(queryResults.features)) {
      featureCacheRef.current = queryResults.features;
      currentLayerNameRef.current = queryResults.layer || 'export';
    }
  }, [queryResults]);

  // Handle window resize manually when needed
  // Removed automatic resize observer to prevent markers from clearing
  // Map has fixed height (600px) so doesn't need automatic invalidateSize

  // Handle query results
  useEffect(() => {
    console.log('Map received queryResults:', queryResults);

    if (!mapRef.current) {
      console.log('Skipping map update: Map not initialized');
      return;
    }

    // Handle explicit null (CLEAR button pressed)
    if (queryResults === null) {
      console.log('🧹 Clearing map: CLEAR button pressed');

      // Remove all layers
      if (markersRef.current) {
        mapRef.current.removeLayer(markersRef.current);
        markersRef.current = null;
      }
      if (polygonsRef.current) {
        mapRef.current.removeLayer(polygonsRef.current);
        polygonsRef.current = null;
      }
      if (labelsLayerRef.current) {
        mapRef.current.removeLayer(labelsLayerRef.current);
        labelsLayerRef.current = null;
      }
      return;
    }

    if (!queryResults) {
      console.log('Skipping map update: queryResults undefined');
      return;
    }

    const { features, extent, zoom, layer, proximityData } = queryResults;
    console.log('Processing map update:', {
      featuresCount: features?.length,
      hasExtent: !!extent,
      zoom,
      layer,
      proximityData: !!proximityData
    });

    // Handle proximity queries specially
    const isProximityQuery = layer === 'proximity' && proximityData;

    // Clear previous markers, polygons, and labels
    if (markersRef.current) {
      mapRef.current.removeLayer(markersRef.current);
    }
    if (polygonsRef.current) {
      mapRef.current.removeLayer(polygonsRef.current);
    }
    if (labelsLayerRef.current) {
      mapRef.current.removeLayer(labelsLayerRef.current);
    }

    // Create polygon layer (always visible, not clustered)
    const polygonsGroup = L.layerGroup().addTo(mapRef.current);
    polygonsRef.current = polygonsGroup;

    // Create labels layer group (will be added/removed based on zoom)
    const labelsLayer = L.layerGroup();
    labelsLayerRef.current = labelsLayer;

    // Create markers layer with clustering (only for pin markers)
    const markersGroup = L.markerClusterGroup({
      showCoverageOnHover: false,
      zoomToBoundsOnClick: true,
      spiderfyOnMaxZoom: true,
      removeOutsideVisibleBounds: true,
      maxClusterRadius: 80,
      disableClusteringAtZoom: 15, // Show individual markers at zoom level 15+
      iconCreateFunction: function(cluster) {
        const count = cluster.getChildCount();
        let className = 'marker-cluster-';
        if (count < 5) {
          className += 'small';
        } else if (count < 10) {
          className += 'medium';
        } else {
          className += 'large';
        }
        return L.divIcon({
          html: `<div><span>${count}</span></div>`,
          className: 'marker-cluster ' + className,
          iconSize: L.point(40, 40)
        });
      }
    }).addTo(mapRef.current);
    markersRef.current = markersGroup;

    // Draw proximity radius circles if this is a proximity query
    if (isProximityQuery && proximityData.centerTracts) {
      const { centerTracts, radius, unit } = proximityData;
      const radiusInMeters = unit === 'kilometers' ? radius * 1000 : milesToMeters(radius);

      // Draw a circle for each center tract
      centerTracts.forEach((centerTractData, idx) => {
        const centerLatLon = [centerTractData.centroid.lat, centerTractData.centroid.lon];

        const circle = L.circle(centerLatLon, {
          radius: radiusInMeters,
          color: '#4ECDC4',
          fillColor: '#4ECDC4',
          fillOpacity: 0.1,
          weight: 2,
          dashArray: '5, 10'
        }).addTo(polygonsGroup);

        console.log(`Drew proximity circle ${idx + 1}:`, {
          center: centerLatLon,
          radius: radius + ' ' + unit,
          radiusInMeters
        });
      });
    }

    // Add center tracts if this is a proximity query
    if (isProximityQuery && proximityData.centerTracts) {
      const { centerTracts } = proximityData;

      centerTracts.forEach((centerTractData, idx) => {
        const centerFeature = centerTractData.feature;
        const props = centerFeature.attributes || centerFeature.properties || {};
        const displayName = props.TractID || props.WellName || `Center Feature ${idx + 1}`;
        const rings = centerFeature.geometry.rings;

        // Convert rings to Leaflet format
        const latlngs = rings.map(ring =>
          ring.map(coord => [coord[1], coord[0]]) // Swap [lon, lat] to [lat, lon]
        );

        // Create center tract polygon with special styling (blue)
        const centerPolygon = L.polygon(latlngs, {
          color: '#2196F3',
          fillColor: '#2196F3',
          fillOpacity: 0.3,
          weight: 3
        }).addTo(polygonsGroup);

        centerPolygon.on('click', () => {
          if (onFeatureClick) {
            onFeatureClick(centerFeature, 'tracts');
          }
        });

        // Add center pin marker
        const centroid = centerPolygon.getBounds().getCenter();
        const centerPinMarker = L.marker(centroid, {
          icon: L.icon({
            iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-blue.png',
            shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.3.4/images/marker-shadow.png',
            iconSize: [25, 41],
            iconAnchor: [12, 41],
            popupAnchor: [1, -34],
            shadowSize: [41, 41]
          })
        }).addTo(markersGroup);

        centerPinMarker.on('click', () => {
          if (onFeatureClick) {
            onFeatureClick(centerFeature, 'tracts');
          }
        });

        // Add center tract label
        const centerLabel = L.marker(centroid, {
          icon: L.divIcon({
            className: 'tract-label',
            html: `<div style="background: white; padding: 2px 6px; border-radius: 3px; font-size: 11px; font-weight: bold; border: 1px solid #2196F3; white-space: nowrap; box-shadow: 0 2px 4px rgba(0,0,0,0.2); margin-top: -50px;">${displayName} (CENTER)</div>`,
            iconSize: [null, null]
          })
        }).addTo(labelsLayer);

        console.log(`Added center tract ${idx + 1}:`, displayName);
      });
    }

    // Convert Web Mercator to WGS84
    const toLatLon = (x, y) => {
      const lon = (x / 20037508.34) * 180;
      let lat = (y / 20037508.34) * 180;
      lat = 180 / Math.PI * (2 * Math.atan(Math.exp(lat * Math.PI / 180)) - Math.PI / 2);
      return [lat, lon];
    };

    // Multi-layer color palette
    const layerColors = {
      tracts: '#3388ff',     // Blue - primary tracts
      wells: '#e74c3c',      // Red - wells
      contracts: '#9b59b6',  // Purple - contracts
      dsus: '#27ae60',       // Green - DSUs
      blocks: '#95a5a6',     // Gray - blocks
      sections: '#7f8c8d',   // Dark gray - sections
      activeRigs: '#f39c12'  // Orange - active rigs
    };

    // Marker icon URLs by layer color
    const markerIcons = {
      tracts: 'blue',
      wells: 'red',
      contracts: 'violet',
      dsus: 'green',
      blocks: 'grey',
      sections: 'grey',
      activeRigs: 'orange'
    };

    // Helper function to get marker color based on dynamic symbology
    const getMarkerColorForFeature = (sourceLayer, props) => {
      // For tracts, map TractType to marker color
      if (sourceLayer === 'tracts' && props.TractType) {
        const typeColorMap = {
          'F': 'blue',      // Fee - primary ownership
          'O': 'violet',    // ORRI - royalty (purple)
          'N': 'green',     // Non-participating
          'U': 'grey',      // Undivided
          'Q': 'blue',      // Qualified (cyan → blue)
          'V': 'orange',    // Vendor
          'OA': 'violet',
          'RF': 'blue',
          'RC': 'violet'
        };
        return typeColorMap[props.TractType] || 'blue';
      }
      // For wells, map ENVWellStatus to marker color
      if (sourceLayer === 'wells' && props.ENVWellStatus) {
        const statusColorMap = {
          'PRODUCING': 'green',
          'DUC': 'yellow',
          'DRILLING': 'red',
          'PERMITTED': 'blue',
          'COMPLETED': 'blue',
          'INACTIVE PRODUCER': 'grey',
          'DRILLED': 'orange',
          'PERMIT EXPIRED': 'grey',
          'PERMIT CANCELLED': 'grey',
          'P & A': 'grey',
          'SHUT-IN': 'grey'
        };
        return statusColorMap[props.ENVWellStatus] || 'red';
      }
      // For emissions leaks, map grade to severity color (Grade 3 = red, 2 = orange, 1 = yellow)
      if (sourceLayer === 'leaks' && (props.grade !== undefined || props.grade_label)) {
        const g = props.grade || (props.grade_label && parseInt(String(props.grade_label).match(/\d/)?.[0] || '0', 10));
        if (g === 3) return 'red';
        if (g === 2) return 'orange';
        if (g === 1) return 'yellow';
      }
      // Sensitive premises: distinct icon per type (school vs hospital)
      if (sourceLayer === 'sensitive_premises') {
        if ((props.address_type || '').toLowerCase().includes('school')) return 'blue';
        if ((props.address_type || '').toLowerCase().includes('hospital')) return 'violet';
        return 'green';
      }
      // Fenceline sensors: triggered = red, normal = green
      if (sourceLayer === 'sensors') {
        return props.communication_status === 'triggered' ? 'red' : 'green';
      }
      // Fallback to layer-based color
      return markerIcons[sourceLayer] || 'red';
    };

    // Per-feature line/polygon color override for emissions layers (pipe risk, etc).
    const getLineStyleForFeature = (sourceLayer, props) => {
      if (sourceLayer === 'pipe_risk') {
        const tier = (props.risk_tier || '').toUpperCase();
        if (tier === 'P1' || tier === 'CRITICAL' || tier === 'HIGH') return { color: '#dc2626', weight: 4 };
        if (tier === 'P2' || tier === 'MEDIUM')                       return { color: '#ea580c', weight: 3 };
        if (tier === 'P3' || tier === 'LOW')                          return { color: '#ca8a04', weight: 2 };
        return { color: '#94a3b8', weight: 2 };
      }
      if (sourceLayer === 'facility' || sourceLayer === 'facility_boundary') {
        // PM #6: needs to be unmistakably visible. Bold red outline + light pink fill.
        return { color: '#dc2626', weight: 4, fillColor: '#fca5a5', fillOpacity: 0.18, dashArray: '8 6' };
      }
      if (sourceLayer === 'dispersion_plumes') {
        return { color: '#f59e0b', weight: 2, fillColor: '#fbbf24', fillOpacity: 0.22, dashArray: '4 4' };
      }
      if (sourceLayer === 'process_units') {
        return { color: '#7c3aed', weight: 2, fillColor: '#c4b5fd', fillOpacity: 0.3 };
      }
      if (sourceLayer === 'districts') {
        return { color: '#475569', weight: 2, fillColor: '#94a3b8', fillOpacity: 0.05, dashArray: '4 3' };
      }
      return null;
    };

    // Skip permanent feature labels when there are too many features to read.
    // Below the threshold, labels are useful; above it, they flood the map.
    const showLabels = features.length <= 30;

    // Add polygons and markers for results
    features.forEach((feature, idx) => {
      const props = feature.attributes || feature.properties || {};
      // Display-name fallback: TPL fields first, then emissions/water fields, then generic id/name.
      const displayName = props.TractID || props.WellName || props.API || props.BlkSec || props.SURVEYBLO3 || props.NAME
        || props.facility_name || props.name || props.title || props.event_id || props.id
        || 'Unknown';

      // Get source layer for multi-layer styling
      const sourceLayer = feature._sourceLayer || layer;
      const featureColor = layerColors[sourceLayer] || '#FF6B6B';
      const markerColor = getMarkerColorForFeature(sourceLayer, props);

      // Handle ArcGIS Polygon format (geometry.rings)
      if (feature.geometry && feature.geometry.rings) {
        const rings = feature.geometry.rings;

        // Convert ArcGIS rings to Leaflet polygon coordinates [lat, lon]
        const latlngs = rings.map(ring =>
          ring.map(coord => [coord[1], coord[0]]) // Swap [lon, lat] to [lat, lon]
        );

        if (idx === 0) {
          console.log('ArcGIS Polygon format detected:', {
            rings: rings.length,
            firstRing: rings[0].length + ' points',
            displayName
          });
        }

        // Determine polygon style based on feature attributes (dynamic symbology)
        let polygonStyle = {
          color: featureColor,
          fillColor: featureColor,
          fillOpacity: 0.2,
          weight: 2
        };

        // Apply attribute-driven symbology for tracts (by TractType)
        if (sourceLayer === 'tracts' && props.TractType) {
          // Check for user overrides first
          const tractOverride = symbologyOverrides?.tractTypes?.[props.TractType];
          if (tractOverride) {
            polygonStyle = {
              color: tractOverride,
              fillColor: tractOverride,
              fillOpacity: 0.4,
              weight: 2
            };
          } else {
            polygonStyle = symbologyConfig.getTractStyle(props.TractType);
          }
        }
        // Apply attribute-driven symbology for wells (by ENVWellStatus)
        else if (sourceLayer === 'wells' && props.ENVWellStatus) {
          // Check for user overrides first
          const wellOverride = symbologyOverrides?.wellStatuses?.[props.ENVWellStatus];
          const baseStyle = symbologyConfig.getWellStyle(props.ENVWellStatus);
          polygonStyle = {
            color: wellOverride || baseStyle.color,
            fillColor: wellOverride || baseStyle.color,
            fillOpacity: 0.3,
            weight: 2
          };
        }

        // Override with proximity-specific styling if applicable
        if (isProximityQuery) {
          // Green for nearby tracts in proximity query
          polygonStyle.color = '#4CAF50';
          polygonStyle.fillColor = '#4CAF50';
        }

        // Create polygon with styling
        const polygon = L.polygon(latlngs, polygonStyle);

        // eslint-disable-next-line no-unused-vars
        const service = featureServices[layer];
        polygon.bindPopup(featurePopupHtml(displayName, props, polygonStyle.color), { maxWidth: 320 });
        polygon.addTo(polygonsGroup); // Add to polygons layer (not clustered)

        // Add click handler to polygon (use source layer for multi-layer)
        polygon.on('click', () => {
          if (onFeatureClick) {
            onFeatureClick(feature, sourceLayer || layer);
          }
        });

        // Add a pin marker at the centroid (this gets clustered)
        const centroid = polygon.getBounds().getCenter();
        const pinMarker = L.marker(centroid, {
          icon: L.icon({
            iconUrl: `https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-${markerColor}.png`,
            shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.3.4/images/marker-shadow.png',
            iconSize: [25, 41],
            iconAnchor: [12, 41],
            popupAnchor: [1, -34],
            shadowSize: [41, 41]
          })
        });

        pinMarker.bindPopup(featurePopupHtml(displayName, props, polygonStyle.color), { maxWidth: 320 });
        pinMarker.addTo(markersGroup); // Add to cluster group

        // Add click handler to pin marker (use source layer for multi-layer)
        pinMarker.on('click', () => {
          if (onFeatureClick) {
            onFeatureClick(feature, sourceLayer || layer);
          }
        });

        // Add a text label near the pin (shown only at high zoom levels)
        // Include distance for proximity queries, or layer name for multi-layer
        let labelText = displayName;
        let labelBorderColor = polygonStyle.color; // Use dynamic symbology color

        // For multi-layer queries, add layer type to label
        const isMultiLayer = queryResults.isMultiLayer || queryResults.layer === 'multi';
        if (isMultiLayer && sourceLayer) {
          const layerLabel = sourceLayer === 'tracts' ? '📍' :
                            sourceLayer === 'wells' ? '🛢️' :
                            sourceLayer === 'contracts' ? '📋' :
                            sourceLayer === 'dsus' ? '🏗️' : '';
          labelText = `${layerLabel} ${displayName}`;
        }

        if (isProximityQuery && proximityData.nearbyTracts) {
          // Find this tract in nearbyTracts to get distance
          const nearbyTract = proximityData.nearbyTracts.find(nt => {
            const ntProps = nt.feature.attributes || nt.feature.properties || {};
            return (ntProps.TractID && ntProps.TractID === props.TractID) ||
                   (ntProps.WellName && ntProps.WellName === props.WellName);
          });

          if (nearbyTract) {
            const unit = proximityData.unit === 'kilometers' ? 'km' : 'mi';
            labelText = `${displayName} (${nearbyTract.distance} ${unit})`;
            labelBorderColor = '#4CAF50'; // Green border for proximity results
          }
        }

        const label = L.marker(centroid, {
          icon: L.divIcon({
            className: 'tract-label',
            html: `<div style="background: white; padding: 2px 6px; border-radius: 3px; font-size: 11px; font-weight: bold; border: 1px solid ${labelBorderColor}; white-space: nowrap; box-shadow: 0 2px 4px rgba(0,0,0,0.2); margin-top: -50px; cursor: pointer;">${labelText}</div>`,
            iconSize: [null, null]
          })
        });
        // Add click handler to label (same as pin marker)
        label.on('click', () => {
          if (onFeatureClick) {
            onFeatureClick(feature, sourceLayer || layer);
          }
        });
        if (showLabels) label.addTo(labelsLayer); // Hide labels above feature-count threshold

        console.log(`Adding polygon + pin ${idx}:`, displayName, `with ${latlngs[0].length} points`);
      }
      // Handle ArcGIS Point format (geometry.x, geometry.y)
      else if (feature.geometry && feature.geometry.x !== undefined && feature.geometry.y !== undefined) {
        // Check if already in WGS84 (lon: -180 to 180, lat: -90 to 90)
        const isWGS84 = Math.abs(feature.geometry.x) <= 180 && Math.abs(feature.geometry.y) <= 90;

        let latLng;
        if (isWGS84) {
          // Already WGS84 - just swap to [lat, lon] for Leaflet
          latLng = [feature.geometry.y, feature.geometry.x];
        } else {
          // Web Mercator - convert to WGS84
          latLng = toLatLon(feature.geometry.x, feature.geometry.y);
        }

        if (idx === 0) {
          console.log('ArcGIS Point format detected:', {
            x: feature.geometry.x,
            y: feature.geometry.y,
            isWGS84,
            latLng
          });
        }

        const marker = L.marker(latLng, {
          icon: L.icon({
            iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png',
            shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.3.4/images/marker-shadow.png',
            iconSize: [25, 41],
            iconAnchor: [12, 41],
            popupAnchor: [1, -34],
            shadowSize: [41, 41]
          })
        });

        // eslint-disable-next-line no-unused-vars
        const service = featureServices[layer];
        marker.bindPopup(featurePopupHtml(displayName, props, featureColor), { maxWidth: 320 });
        marker.addTo(markersGroup);

        // Add click handler to marker
        marker.on('click', () => {
          if (onFeatureClick) {
            onFeatureClick(feature, layer);
          }
        });

        console.log(`Adding marker ${idx} at:`, latLng, 'for', displayName);
      }
      // Handle GeoJSON format (geometry.coordinates)
      else if (feature.geometry && feature.geometry.coordinates) {
        const coords = feature.geometry.coordinates;
        const geomType = feature.geometry.type;

        // Handle GeoJSON Polygon - real TPL tract data
        if (geomType === 'Polygon' || geomType === 'MultiPolygon') {
          // Determine style based on layer
          const layerStyle = featureServices[layer]?.style || {};
          const isOutlineOnly = featureServices[layer]?.outlineOnly;
          const emissionsStyle = getLineStyleForFeature(sourceLayer, props);

          const style = emissionsStyle || {
            color: layerStyle.color || '#FF6B6B',
            fillColor: isOutlineOnly ? 'transparent' : (layerStyle.fillColor || '#FF6B6B'),
            fillOpacity: isOutlineOnly ? 0 : (layerStyle.fillOpacity || 0.2),
            weight: layerStyle.weight || 2,
            dashArray: layerStyle.dashArray || null
          };

          // Create GeoJSON layer for polygon
          const geoJsonLayer = L.geoJSON(feature, { style });
          geoJsonLayer.addTo(polygonsGroup);

          // Add click handler
          geoJsonLayer.on('click', () => {
            if (onFeatureClick) {
              onFeatureClick(feature, layer);
            }
          });

          // Add pin marker at centroid (unless outline-only)
          if (!isOutlineOnly) {
            const bounds = geoJsonLayer.getBounds();
            const centroid = bounds.getCenter();

            const pinMarker = L.marker(centroid, {
              icon: L.icon({
                iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png',
                shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.3.4/images/marker-shadow.png',
                iconSize: [25, 41],
                iconAnchor: [12, 41],
                popupAnchor: [1, -34],
                shadowSize: [41, 41]
              })
            });
            pinMarker.bindPopup(featurePopupHtml(displayName, props, style.color), { maxWidth: 320 });
            pinMarker.addTo(markersGroup);
            pinMarker.on('click', () => {
              if (onFeatureClick) {
                onFeatureClick(feature, layer);
              }
            });

            // Add label
            const label = L.marker(centroid, {
              icon: L.divIcon({
                className: 'tract-label',
                html: `<div style="background: white; padding: 2px 6px; border-radius: 3px; font-size: 11px; font-weight: bold; border: 1px solid ${style.color}; white-space: nowrap; box-shadow: 0 2px 4px rgba(0,0,0,0.2); margin-top: -50px; cursor: pointer;">${displayName}</div>`,
                iconSize: [null, null]
              })
            });
            // Add click handler to label
            label.on('click', () => {
              if (onFeatureClick) {
                onFeatureClick(feature, sourceLayer || layer);
              }
            });
            if (showLabels) label.addTo(labelsLayer);
          }

          if (idx === 0) {
            console.log(`Adding GeoJSON ${geomType}:`, displayName, 'with style', style);
          }
        }
        // Handle GeoJSON LineString - well laterals
        else if (geomType === 'LineString' || geomType === 'MultiLineString') {
          // Apply dynamic symbology for wells based on ENVWellStatus
          let lineStyle;
          const emissionsLineStyle = getLineStyleForFeature(sourceLayer, props);
          if (emissionsLineStyle) {
            lineStyle = emissionsLineStyle;
          } else if (sourceLayer === 'wells' && props.ENVWellStatus) {
            // Check for user overrides first
            const wellOverride = symbologyOverrides?.wellStatuses?.[props.ENVWellStatus];
            const baseStyle = symbologyConfig.getWellStyle(props.ENVWellStatus);
            lineStyle = {
              color: wellOverride || baseStyle.color,
              weight: baseStyle.weight || 3
            };
          } else {
            const layerStyle = featureServices[layer]?.style || {};
            lineStyle = {
              color: layerStyle.color || '#e74c3c',
              weight: layerStyle.weight || 3
            };
          }

          // Create GeoJSON layer for line
          const geoJsonLayer = L.geoJSON(feature, { style: lineStyle });
          geoJsonLayer.addTo(polygonsGroup);

          // Add click handler
          geoJsonLayer.on('click', () => {
            if (onFeatureClick) {
              onFeatureClick(feature, layer);
            }
          });

          // Add marker at line center for clustering (with dynamic color)
          const bounds = geoJsonLayer.getBounds();
          const center = bounds.getCenter();
          const lineMarkerColor = getMarkerColorForFeature(sourceLayer, props);

          const lineMarker = L.marker(center, {
            icon: L.icon({
              iconUrl: `https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-${lineMarkerColor}.png`,
              shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.3.4/images/marker-shadow.png',
              iconSize: [20, 33],
              iconAnchor: [10, 33],
              popupAnchor: [1, -28],
              shadowSize: [33, 33]
            })
          });
          lineMarker.bindPopup(featurePopupHtml(displayName, props, lineStyle.color), { maxWidth: 320 });
          lineMarker.addTo(markersGroup);
          lineMarker.on('click', () => {
            if (onFeatureClick) {
              onFeatureClick(feature, layer);
            }
          });

          // Add well name label with dynamic color
          const label = L.marker(center, {
            icon: L.divIcon({
              className: 'well-label',
              html: `<div style="background: white; padding: 2px 6px; border-radius: 3px; font-size: 10px; font-weight: bold; border: 1px solid ${lineStyle.color}; white-space: nowrap; box-shadow: 0 2px 4px rgba(0,0,0,0.2); margin-top: -40px; cursor: pointer;">${displayName}</div>`,
              iconSize: [null, null]
            })
          });
          // Add click handler to label
          label.on('click', () => {
            if (onFeatureClick) {
              onFeatureClick(feature, sourceLayer || layer);
            }
          });
          if (showLabels) label.addTo(labelsLayer);

          if (idx === 0) {
            console.log(`Adding GeoJSON ${geomType} (well lateral):`, displayName);
          }
        }
        // Handle GeoJSON Point
        else if (geomType === 'Point') {
          const isWebMercator = Math.abs(coords[0]) > 200 || Math.abs(coords[1]) > 200;
          let latLng;
          if (isWebMercator) {
            latLng = toLatLon(coords[0], coords[1]);
          } else {
            latLng = [coords[1], coords[0]];
          }

          // PM #6: triggered sensors must be unmissable. Use pulsing divIcon for triggered sensors.
          const isTriggeredSensor = sourceLayer === 'sensors' && props.communication_status === 'triggered';
          let icon;
          if (isTriggeredSensor) {
            icon = L.divIcon({
              className: 'scada-marker-icon',
              html: `<div class="scada-marker scada-marker-pulse" style="--scada-color:#dc2626">
                       <div class="scada-marker-dot"></div>
                       <div class="scada-marker-ring"></div>
                     </div>`,
              iconSize: [28, 28],
              iconAnchor: [14, 14]
            });
          } else {
            const colorName = markerColor || 'red';
            icon = L.icon({
              iconUrl: `https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-${colorName}.png`,
              shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.3.4/images/marker-shadow.png',
              iconSize: [25, 41],
              iconAnchor: [12, 41],
              popupAnchor: [1, -34],
              shadowSize: [41, 41]
            });
          }
          const marker = L.marker(latLng, { icon });

          marker.bindPopup(featurePopupHtml(displayName, props, featureColor), { maxWidth: 320 });
          marker.addTo(markersGroup);
          marker.on('click', () => {
            if (onFeatureClick) {
              onFeatureClick(feature, layer);
            }
          });

          console.log(`Adding GeoJSON Point ${idx} at:`, latLng, 'for', displayName);
        }
      }
    });

    // Smart zoom logic: Check if we should auto-zoom
    const shouldAutoZoom = () => {
      // Always zoom if zoom flag is true
      if (zoom) return true;
      if (features.length === 0) return false;

      // Smart auto-zoom: Check if new features are outside current viewport
      const currentBounds = mapRef.current.getBounds();
      const markersGroup = L.featureGroup(markersRef.current.getLayers());

      if (markersGroup.getLayers().length === 0) return false;

      const featureBounds = markersGroup.getBounds();

      // If feature bounds don't intersect with current view, auto-zoom
      const isOutsideView = !currentBounds.intersects(featureBounds);

      if (isOutsideView) {
        console.log('🎯 Smart auto-zoom: Features outside current viewport (county switch detected)');
        return true;
      }

      return false;
    };

    const needsZoom = shouldAutoZoom();

    // Zoom to extent if provided
    if (needsZoom && extent) {
      // Check if extent is already in WGS84
      const isWGS84 = Math.abs(extent.xmin) <= 180 && Math.abs(extent.ymin) <= 90;

      let sw, ne;
      if (isWGS84) {
        // Already WGS84 - use directly as [lat, lon]
        sw = [extent.ymin, extent.xmin];
        ne = [extent.ymax, extent.xmax];
      } else {
        // Web Mercator - convert to WGS84
        const toLatLon = (x, y) => {
          const lon = (x / 20037508.34) * 180;
          let lat = (y / 20037508.34) * 180;
          lat = 180 / Math.PI * (2 * Math.atan(Math.exp(lat * Math.PI / 180)) - Math.PI / 2);
          return [lat, lon];
        };
        sw = toLatLon(extent.xmin, extent.ymin);
        ne = toLatLon(extent.xmax, extent.ymax);
      }

      const bounds = [sw, ne];
      console.log('Zoom to bounds:', { isWGS84, extent, bounds });
      mapRef.current.fitBounds(bounds, { padding: [50, 50] });
    } else if (needsZoom && features.length > 0) {
      // If no extent but should zoom, fit to markers
      const group = L.featureGroup(markersRef.current.getLayers());
      mapRef.current.fitBounds(group.getBounds(), { padding: [50, 50] });
      console.log('Zooming to markers (smart auto-zoom or zoom flag)');
    } else {
      console.log('Not zooming:', { zoom, hasExtent: !!extent, featureCount: features.length });
    }

    // Function to update label visibility based on zoom
    const updateLabelVisibility = () => {
      const currentZoom = mapRef.current.getZoom();
      if (currentZoom >= 13) {
        // Add labels to map at high zoom
        if (!mapRef.current.hasLayer(labelsLayer)) {
          labelsLayer.addTo(mapRef.current);
        }
      } else {
        // Remove labels from map at low zoom
        if (mapRef.current.hasLayer(labelsLayer)) {
          mapRef.current.removeLayer(labelsLayer);
        }
      }
    };

    // Set initial visibility based on current zoom
    updateLabelVisibility();

    // Add zoom event listener
    const handleZoomEnd = () => {
      updateLabelVisibility();
    };

    mapRef.current.on('zoomend', handleZoomEnd);

    // Cleanup function to remove listener
    return () => {
      if (mapRef.current) {
        mapRef.current.off('zoomend', handleZoomEnd);
      }
    };

  }, [queryResults, symbologyOverrides]);

  // Manually toggle a layer on/off — fetch features and dispatch to App.js which
  // sets queryResults to a synthetic multi-layer payload. Lets the demo work the
  // Layers panel without first running a chat query.
  const toggleLayer = async (layerKey) => {
    const isOn = !!layerVisibility[layerKey];
    if (onLayerToggle) {
      onLayerToggle(layerKey, !isOn);
    }
  };

  return (
    <div className="map-wrapper">
      <div ref={mapContainerRef} className="map-container" style={{ height: '600px', width: '100%' }} />

      {/* Layer Control */}
      <LayerControl
        layerVisibility={layerVisibility}
        toggleLayer={toggleLayer}
        getLayerColor={getLayerColor}
        importedLayers={importedLayers}
        onRemoveImported={(id) => mapApiRef?.current?.removeImportedLayer?.(id)}
      />

      {/* Results Info — multi-layer proximity shows leak count + nearby premise count for coherence */}
      {queryResults && (() => {
        const features = queryResults.features || [];
        const leakCount = features.filter(f => (f._sourceLayer === 'leaks') || (f._sourceLayer === undefined && f.properties?.grade !== undefined)).length;
        const premiseCount = features.filter(f => f._sourceLayer === 'sensitive_premises').length;
        const isProximity = queryResults.proximity_to || (premiseCount > 0 && leakCount > 0);
        return (
          <div className="results-info">
            {isProximity ? (
              <><strong>{leakCount}</strong> leak{leakCount === 1 ? '' : 's'} · {premiseCount} nearby premise{premiseCount === 1 ? '' : 's'}</>
            ) : (
              <><strong>{queryResults.count}</strong> features found</>
            )}
            {queryResults.count > 0 && queryResults.zoom && ' (zoomed to extent)'}
          </div>
        );
      })()}

      {/* Color Legend */}
      <MapLegend visibleLayers={layerVisibility} />
    </div>
  );
}

// ─── Layer Control: button-driven popover with grouped layers ───────────────
const EMISSIONS_LAYER_GROUPS = [
  { id: 'survey',     label: 'Survey & Leaks',      layers: ['leaks', 'survey_routes'] },
  { id: 'pipes',      label: 'Pipe Network',        layers: ['distribution_mains', 'pipe_risk'] },
  { id: 'ops',        label: 'Operations',          layers: ['crews', 'work_orders_open', 'sensitive_premises', 'districts'] },
  { id: 'fenceline',  label: 'Fenceline & Refinery', layers: ['sensors', 'facility', 'process_units', 'dispersion_plumes'] },
];

function LayerControl({ layerVisibility, toggleLayer, getLayerColor, importedLayers = [], onRemoveImported }) {
  const [open, setOpen] = useState(false);
  const [openGroups, setOpenGroups] = useState({});
  const popRef = useRef(null);

  // Click-outside dismiss
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (popRef.current && !popRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const visibleCount = Object.values(layerVisibility).filter(Boolean).length;
  const groups = isEmissionsTenant
    ? EMISSIONS_LAYER_GROUPS
    : [{ id: 'all', label: 'All Layers', layers: Object.keys(featureServices) }];

  // Filter to only layers that exist in featureServices for this tenant
  const filteredGroups = groups.map(g => ({
    ...g,
    layers: g.layers.filter(k => featureServices[k]),
  })).filter(g => g.layers.length > 0);

  const toggleGroup = (gid) =>
    setOpenGroups(prev => ({ ...prev, [gid]: !prev[gid] }));

  return (
    <div className="layer-control-wrap" ref={popRef}>
      <button
        className={`layer-control-btn ${open ? 'active' : ''}`}
        onClick={() => setOpen(v => !v)}
        aria-label="Toggle layers panel"
      >
        <span className="lc-btn-icon">⊞</span>
        <span className="lc-btn-label">Layers</span>
        {visibleCount > 0 && <span className="lc-btn-badge">{visibleCount}</span>}
      </button>

      {open && (
        <div className="layer-control-panel">
          <div className="lc-header">
            <span>Layers</span>
            <button className="lc-close" onClick={() => setOpen(false)} aria-label="Close">×</button>
          </div>
          {filteredGroups.map(g => {
            const groupOpen = openGroups[g.id] !== false; // open by default
            const onCount = g.layers.filter(k => layerVisibility[k]).length;
            return (
              <div key={g.id} className="lc-group">
                <button className="lc-group-head" onClick={() => toggleGroup(g.id)}>
                  <span className={`lc-chev ${groupOpen ? 'open' : ''}`}>▸</span>
                  <span className="lc-group-label">{g.label}</span>
                  <span className="lc-group-count">{onCount}/{g.layers.length}</span>
                </button>
                {groupOpen && (
                  <div className="lc-group-body">
                    {g.layers.map(layerKey => {
                      const service = featureServices[layerKey];
                      const isChecked = !!layerVisibility[layerKey];
                      return (
                        <label key={layerKey} className="layer-toggle">
                          <input type="checkbox" checked={isChecked} onChange={() => toggleLayer(layerKey)} />
                          <span className="layer-dot" style={{ color: getLayerColor(layerKey) }}>●</span>
                          <span className="layer-name">{service.name}</span>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}

          {/* Imported layers (PM #7) — per-layer remove button */}
          {importedLayers.length > 0 && (
            <div className="lc-group" style={{ borderTop: '1px solid rgba(148,163,184,0.25)', marginTop: 6, paddingTop: 6 }}>
              <div className="lc-group-head" style={{ cursor: 'default' }}>
                <span className="lc-group-label" style={{ color: '#7c3aed' }}>Imported</span>
                <span className="lc-group-count">{importedLayers.length}</span>
              </div>
              <div className="lc-group-body">
                {importedLayers.map((imp) => (
                  <div key={imp.id} className="layer-toggle" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span className="layer-dot" style={{ color: '#7c3aed' }}>●</span>
                    <span className="layer-name" style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={`${imp.name} · ${imp.count} features`}>
                      {imp.name} <span style={{ color: '#94a3b8', fontSize: 11 }}>· {imp.count}</span>
                    </span>
                    <button
                      onClick={() => onRemoveImported && onRemoveImported(imp.id)}
                      title="Remove imported layer"
                      style={{
                        background: 'transparent', border: 'none', color: '#dc2626',
                        cursor: 'pointer', fontSize: 16, padding: '0 4px', lineHeight: 1
                      }}
                      aria-label={`Remove ${imp.name}`}
                    >×</button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Compact popup HTML for a feature: title + key/value table for the most relevant fields.
 * Skips internal/noisy fields (lat, lon, _sourceLayer, geometry, type).
 */
function featurePopupHtml(displayName, props, color = '#2563eb') {
  const skip = new Set(['lat', 'lon', '_sourceLayer', 'geometry', 'type']);
  const formatVal = (v) => {
    if (v === null || v === undefined || v === '') return '—';
    if (typeof v === 'number') {
      return Number.isInteger(v) ? v.toString() : v.toFixed(2);
    }
    if (typeof v === 'object') return JSON.stringify(v);
    return String(v);
  };
  const formatKey = (k) =>
    k.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  const rows = Object.entries(props || {})
    .filter(([k, v]) => !skip.has(k) && v !== null && v !== undefined && v !== '')
    .slice(0, 12)
    .map(
      ([k, v]) =>
        `<tr><td style="padding:2px 8px 2px 0;color:#64748b;font-size:11px;vertical-align:top;white-space:nowrap;">${formatKey(k)}</td><td style="padding:2px 0;font-size:12px;color:#0f172a;">${formatVal(v)}</td></tr>`
    )
    .join('');
  return `
    <div style="min-width:220px;max-width:320px;font-family:-apple-system,BlinkMacSystemFont,sans-serif;">
      <div style="border-left:3px solid ${color};padding:0 0 0 8px;margin-bottom:6px;">
        <div style="font-size:13px;font-weight:600;color:#0f172a;">${displayName}</div>
      </div>
      <table style="border-collapse:collapse;width:100%;">${rows}</table>
    </div>
  `;
}

/**
 * Generate popup HTML content from feature properties (legacy, kept for compatibility)
 */
// eslint-disable-next-line no-unused-vars
function generatePopupContent(properties, template) {
  if (!template) {
    // Default: show all properties
    return Object.entries(properties)
      .map(([key, value]) => `<strong>${key}:</strong> ${value}`)
      .join('<br/>');
  }

  let content = `<div class="popup-content"><h3>${template.title}</h3>`;

  template.content.forEach(field => {
    const value = properties[field.fieldName];
    if (value !== null && value !== undefined) {
      content += `<p><strong>${field.label}:</strong> ${value}</p>`;
    }
  });

  content += '</div>';

  // Replace placeholders in title
  Object.keys(properties).forEach(key => {
    content = content.replace(`{${key}}`, properties[key]);
  });

  return content;
}

/**
 * Get color for layer
 */
function getLayerColor(layerKey) {
  const colors = {
    tracts: '#3388ff',     // Blue - primary tracts
    wells: '#e74c3c',      // Red - well laterals
    blocks: '#95a5a6',     // Gray - landgrid blocks
    sections: '#7f8c8d',   // Dark gray - survey sections
    contracts: '#9b59b6',  // Purple - easements/contracts
    dsus: '#27ae60',       // Green - development units
    activeRigs: '#f39c12'  // Orange - active rigs
  };
  return colors[layerKey] || '#95A5A6';
}

export default Map;
